import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { Modal, BtnPrimary, fmtBytes } from './NotesModals';
import { GroupShareBlock } from './TasksTemplatesGroups';
import {
  Users2, Share2, Loader2, ChevronDown, Check, X, Eye, Pencil, FolderKanban, Info,
  Archive, Download, Upload,
} from 'lucide-react';

const PERM_OPTS = [
  { value: 'view', label: 'Can view', icon: Eye },
  { value: 'edit', label: 'Can edit', icon: Pencil },
];

// Compact permission menu (self-contained; mirrors Notes styling).
function PermMenu({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const cur = PERM_OPTS.find((p) => p.value === value) || PERM_OPTS[1];
  const CurIcon = cur.icon;
  return (
    <div className="relative shrink-0" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid}
        className="n-focus inline-flex items-center gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
        <CurIcon size={13} /> {cur.label} <ChevronDown size={13} />
      </button>
      {open && (
        <div className="absolute right-0 z-[60] mt-1 w-36 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]">
          {PERM_OPTS.map((p) => {
            const Icon = p.icon;
            return (
              <button key={p.value} type="button" onClick={() => { onChange(p.value); setOpen(false); }} data-testid={`${testid}-opt-${p.value}`}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] hover:bg-[var(--n-canvas-2)] transition-colors ${value === p.value ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
                <Icon size={13} /> {p.label} {value === p.value && <Check size={12} className="ml-auto" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task / Project share dialog — shares cascade for projects (all tasks inside),
// or apply to a single task. Reuses /notes/directory for the people list.
// ---------------------------------------------------------------------------
export function TaskShareDialog({ open, onClose, resource, notify, onChanged }) {
  const [directory, setDirectory] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [permission, setPermission] = useState('edit');
  const [busy, setBusy] = useState(false);
  const pickerRef = useRef(null);

  const rtype = resource?.type; // 'task' | 'project'
  const rid = resource?.id;

  const load = useCallback(async () => {
    if (!rtype || !rid) return;
    setLoading(true);
    try {
      const [dir, sh] = await Promise.all([
        notesApi.get('/notes/directory'),
        notesApi.get(`/notes/tasks/shares?resource_type=${rtype}&resource_id=${rid}`),
      ]);
      setDirectory(dir.data || []);
      setShares(sh.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [rtype, rid]);

  useEffect(() => { if (open) { setSelected([]); setPermission('edit'); setPickerOpen(false); load(); } }, [open, load]);

  useEffect(() => {
    const h = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sharedIds = new Set(shares.map((s) => s.target_user_id));
  const available = directory.filter((u) => !sharedIds.has(u.id));
  const toggleSelect = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const initials = (u) => (u.name || u.email || '?').slice(0, 1).toUpperCase();
  const selectedLabel = selected.length ? `${selected.length} selected` : 'Select people…';

  const addShares = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      for (const uid of selected) {
        await notesApi.post('/notes/tasks/shares', { resource_type: rtype, resource_id: rid, target_user_id: uid, permission });
      }
      const n = selected.length;
      setSelected([]); setPickerOpen(false);
      await load();
      onChanged?.();
      notify?.(`Shared with ${n} ${n === 1 ? 'person' : 'people'}.`);
    } catch (e) { notify?.(e?.response?.data?.detail || 'Could not share.'); } finally { setBusy(false); }
  };

  const changePerm = async (uid, perm) => {
    setShares((p) => p.map((s) => (s.target_user_id === uid ? { ...s, permission: perm } : s)));
    try { await notesApi.post('/notes/tasks/shares', { resource_type: rtype, resource_id: rid, target_user_id: uid, permission: perm }); onChanged?.(); }
    catch { notify?.('Could not update permission.'); load(); }
  };

  const removeShare = async (uid) => {
    setShares((p) => p.filter((s) => s.target_user_id !== uid));
    try { await notesApi.delete(`/notes/tasks/shares?resource_type=${rtype}&resource_id=${rid}&target_user_id=${uid}`); onChanged?.(); }
    catch { notify?.('Could not remove access.'); load(); }
  };

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-lg" testid="task-share-dialog"
      title={`Share ${rtype === 'project' ? 'project' : 'task'}`}
      subtitle={resource?.name ? `“${resource.name}”` : 'Choose who can access this'}>
      {rtype === 'project' && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-[var(--n-accent-soft)] bg-[var(--n-accent-bg)] px-3 py-2.5 text-[12px] text-[var(--n-ink-soft)]" data-testid="task-share-project-hint">
          <FolderKanban size={14} className="mt-0.5 shrink-0 text-[var(--n-accent)]" />
          <span>Sharing a project shares <strong>every task inside it</strong>. New tasks are shared automatically.</span>
        </div>
      )}
      <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3 mb-3">
        <div className="text-xs font-semibold text-[var(--n-ink-soft)] mb-2 flex items-center gap-1.5"><Users2 size={14} /> Add people</div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1" ref={pickerRef}>
            <button type="button" onClick={() => setPickerOpen((o) => !o)} data-testid="task-share-people-picker-button"
              className="n-focus flex w-full items-center justify-between gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm hover:bg-[var(--n-canvas-2)] transition-colors">
              <span className={`truncate ${selected.length ? 'text-[var(--n-ink)]' : 'text-[var(--n-muted-2)]'}`}>{selectedLabel}</span>
              <ChevronDown size={15} className="shrink-0 text-[var(--n-muted-2)]" />
            </button>
            {pickerOpen && (
              <div className="absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="task-share-people-picker-menu">
                {available.length === 0 ? (
                  <div className="px-2.5 py-3 text-center text-[12px] text-[var(--n-muted)]">Everyone already has access.</div>
                ) : available.map((u) => {
                  const on = selected.includes(u.id);
                  return (
                    <button key={u.id} type="button" onClick={() => toggleSelect(u.id)} data-testid="task-share-people-option"
                      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors ${on ? 'bg-[var(--n-accent-bg)]' : ''}`}>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-border)] text-transparent'}`}><Check size={12} /></span>
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[10px] font-bold text-[var(--n-accent)]">{initials(u)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-[var(--n-ink)]">{u.name}</span>
                        <span className="block truncate text-[11px] text-[var(--n-muted)]">{u.email}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <PermMenu value={permission} onChange={setPermission} testid="task-share-add-permission" />
        </div>
        <div className="mt-2 flex justify-end">
          <BtnPrimary onClick={addShares} disabled={busy || !selected.length} data-testid="task-share-add-button">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} Share
          </BtnPrimary>
        </div>
      </div>

      <div className="mb-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">People with access</div>
      {loading ? (
        <div className="flex justify-center py-6 text-[var(--n-muted)]"><Loader2 className="animate-spin" size={18} /></div>
      ) : (
        <div className="space-y-1.5 pb-2" data-testid="task-share-current-list">
          {shares.length === 0 && <div className="text-center text-xs text-[var(--n-muted)] py-5">Not shared with anyone yet.</div>}
          {shares.map((s) => (
            <div key={s.target_user_id} className="flex items-center gap-3 rounded-xl border border-[var(--n-line)] px-3 py-2.5" data-testid="task-share-current-item">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-xs font-bold text-[var(--n-accent)]">{initials(s)}</span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-[var(--n-ink)]">{s.name}</div>
                <div className="truncate text-xs text-[var(--n-muted)]">{s.email}</div>
              </div>
              <PermMenu value={s.permission} onChange={(v) => changePerm(s.target_user_id, v)} testid="task-share-item-permission" />
              <button onClick={() => removeShare(s.target_user_id)} title="Remove access" aria-label="Remove access"
                data-testid="task-share-remove-button" className="text-[var(--n-muted-2)] hover:text-[var(--n-danger)] transition-colors"><X size={16} /></button>
            </div>
          ))}
        </div>
      )}
      {rtype === 'task' && (
        <div className="mt-3">
          <GroupShareBlock taskId={rid} notify={notify} onChanged={onChanged} />
        </div>
      )}
      <div className="mt-1 flex items-start gap-1.5 px-0.5 text-[11px] text-[var(--n-muted-2)]">
        <Info size={12} className="mt-0.5 shrink-0" /> Anyone with access can mark the task complete — it strikes through for everyone in real time.
      </div>
    </Modal>
  );
}


// ---------------------------------------------------------------------------
// Tasks — Super Admin encrypted backup & restore (separate archive from Notes).
// Whole workspace: projects, tasks, sub-tasks, tags, shares, comments,
// activity, filters and recurring series + task attachments.
// ---------------------------------------------------------------------------
export function TasksAdminDialog({ open, onClose }) {
  const [bkPw, setBkPw] = useState('');
  const [bkBusy, setBkBusy] = useState(false);
  const [bkMsg, setBkMsg] = useState(null);
  const [bkBytes, setBkBytes] = useState(0);

  const [rsFile, setRsFile] = useState(null);
  const [rsPw, setRsPw] = useState('');
  const [rsMode, setRsMode] = useState('merge');
  const [rsBusy, setRsBusy] = useState(false);
  const [rsResult, setRsResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setBkPw(''); setBkMsg(null); setBkBytes(0); setRsFile(null); setRsPw('');
      setRsMode('merge'); setRsResult(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [open]);

  const doBackup = async () => {
    if (bkPw.length < 6) return;
    setBkBusy(true); setBkMsg(null); setBkBytes(0);
    try {
      const res = await notesApi.post('/notes/tasks/admin/backup', { password: bkPw }, {
        responseType: 'blob',
        onDownloadProgress: (e) => setBkBytes(e.loaded || 0),
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url; a.download = `tasks-backup-${ts}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setBkMsg({ text: 'Tasks backup downloaded. Keep the password safe — it is required to restore.', kind: 'ok' });
    } catch (e) {
      setBkMsg({ text: e?.response?.data?.detail || 'Backup failed', kind: 'err' });
    } finally { setBkBusy(false); }
  };

  const doRestore = async () => {
    if (!rsFile) { setRsResult({ error: 'Choose a backup .zip file first.' }); return; }
    if ((rsPw || '').length < 6) { setRsResult({ error: 'Enter the backup password.' }); return; }
    if (rsMode === 'replace' && !window.confirm('REPLACE will permanently erase ALL current Tasks data (every user’s projects, tasks, sub-tasks, comments, activity) and import from this backup. Continue?')) return;
    setRsBusy(true); setRsResult(null);
    try {
      const fd = new FormData();
      fd.append('file', rsFile);
      fd.append('password', rsPw);
      fd.append('mode', rsMode);
      const { data } = await notesApi.post('/notes/tasks/admin/restore', fd);
      setRsResult(data);
    } catch (e) {
      setRsResult({ error: e?.response?.data?.detail || 'Restore failed' });
    } finally { setRsBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Tasks backup & restore" maxW="max-w-lg"
      subtitle="Encrypted archive of the entire Tasks workspace" testid="tasks-admin-dialog">
      <div className="space-y-3">
        {/* Backup */}
        <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3" data-testid="tasks-backup-section">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[var(--n-ink-soft)]"><Archive size={14} /> Download backup</div>
          <p className="mb-2 text-[11.5px] leading-relaxed text-[var(--n-muted)]">
            A password-protected ZIP of every user's projects, tasks, sub-tasks, tags, shares, comments,
            activity, filters and recurring series, plus task attachments.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input type="password" value={bkPw} onChange={(e) => setBkPw(e.target.value)}
              placeholder="Set a backup password (min 6)" data-testid="tasks-backup-password-input"
              className="n-focus min-w-[220px] flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
            <BtnPrimary onClick={doBackup} disabled={bkBusy || bkPw.length < 6} data-testid="tasks-backup-download-button">
              {bkBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download
            </BtnPrimary>
          </div>
          {bkBusy && (
            <div className="mt-2" data-testid="tasks-backup-progress">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--n-border)]">
                <div className="h-full w-1/3 rounded-full bg-[var(--n-accent)] n-indeterminate" />
              </div>
              <div className="mt-1 text-[11px] font-medium text-[var(--n-muted)]" data-testid="tasks-backup-progress-text">
                Preparing & streaming… {fmtBytes(bkBytes)} downloaded
              </div>
            </div>
          )}
          {bkMsg && <div className={`mt-1.5 text-[11.5px] font-medium ${bkMsg.kind === 'ok' ? 'text-[var(--n-accent)]' : 'text-[var(--n-danger)]'}`} data-testid="tasks-backup-msg">{bkMsg.text}</div>}
        </div>

        {/* Restore */}
        <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3" data-testid="tasks-restore-section">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-[var(--n-ink-soft)]"><Upload size={14} /> Restore from backup</div>
          <input ref={fileRef} type="file" accept=".zip" data-testid="tasks-restore-file-input"
            onChange={(e) => setRsFile(e.target.files?.[0] || null)}
            className="mb-2 block w-full text-[12px] text-[var(--n-ink-soft)] file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--n-accent-bg)] file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-[var(--n-accent)]" />
          <div className="flex flex-wrap items-center gap-2">
            <input type="password" value={rsPw} onChange={(e) => setRsPw(e.target.value)} placeholder="Backup password" data-testid="tasks-restore-password-input"
              className="n-focus min-w-[180px] flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
            <div className="inline-flex rounded-lg border border-[var(--n-border)] p-0.5" data-testid="tasks-restore-mode">
              {[{ val: 'merge', label: 'Merge' }, { val: 'replace', label: 'Replace' }].map(({ val, label }) => (
                <button key={val} onClick={() => setRsMode(val)} data-testid={`tasks-restore-mode-${val}`}
                  className={`n-focus rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors ${rsMode === val ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>{label}</button>
              ))}
            </div>
            <BtnPrimary onClick={doRestore} disabled={rsBusy || !rsFile || rsPw.length < 6} data-testid="tasks-restore-button">
              {rsBusy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Restore
            </BtnPrimary>
          </div>
          <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[var(--n-muted-2)]">
            <Info size={12} className="mt-0.5 shrink-0" />
            {rsMode === 'replace'
              ? 'Replace erases ALL current Tasks data for every user, then imports from the backup.'
              : 'Merge adds only records that are not already present (safe, non-destructive).'}
          </div>
          {rsResult && rsResult.error && <div className="mt-2 text-[11.5px] font-medium text-[var(--n-danger)]" data-testid="tasks-restore-error">{rsResult.error}</div>}
          {rsResult && !rsResult.error && (
            <div className="mt-2 rounded-lg border border-[var(--n-line)] bg-[var(--n-surface)] p-2 text-[11.5px] text-[var(--n-ink-soft)]" data-testid="tasks-restore-result">
              <div className="mb-1 font-semibold text-[var(--n-accent)]">Restore complete ({rsResult.mode})</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                {Object.entries(rsResult.collections || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-2">
                    <span className="truncate text-[var(--n-muted)]">{k.replace('notes_', '')}</span>
                    <span className="tabular-nums">{v.replaced != null ? `${v.replaced} replaced` : `${v.added} added · ${v.skipped} skipped`}</span>
                  </div>
                ))}
              </div>
              <div className="mt-1 text-[var(--n-muted)]">Attachments restored: {rsResult.attachments_restored}{rsResult.attachment_errors ? ` (${rsResult.attachment_errors} failed)` : ''}</div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

